import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, X, Loader2, Check } from 'lucide-react';
import { unwrapImg } from '../lib/imgUrl';

/**
 * ProductPickerModal — browse SanMar catalog and attach a product to a
 * proposal line item so the ItemCard shows a real garment photo (like InkSoft).
 *
 * Props:
 *   open          – bool
 *   onClose       – () => void
 *   onPick        – ({styleID, styleName, brandName, colorName, colorHex, imageUrl, basePrice, sizes, supplier}) => void
 *
 * Two-step flow:
 *   1. Search styles by name/code → grid of style cards
 *   2. Click a style → color grid; click a color → onPick(...)
 */
const ProductPickerModal = ({ open, onClose, onPick }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery(''); setResults([]); setSelectedStyle(null);
    }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (query || '').trim();
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/sanmar/products/search-multi/${encodeURIComponent(q)}?limit=12`);
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        toast.error('Search failed');
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  if (!open) return null;

  const pickColor = (color) => {
    if (!selectedStyle || !color) return;
    // Prefer the color-specific front photo so ST550 in "Atomic Blue" actually
    // shows the atomic-blue shirt (not the generic style-level default image).
    // Fall back to styleImageUrl only when SanMar has no per-color imagery.
    const primaryImage = unwrapImg(color.imageUrl || selectedStyle.styleImageUrl);
    onPick({
      styleID: selectedStyle.styleID,
      styleName: selectedStyle.styleName,
      brandName: selectedStyle.brandName,
      colorName: color.colorName,
      colorHex: color.hex,
      imageUrl: primaryImage,
      // Per-angle photos so the Mockup Designer can offer 4 tabs
      // (Front / Back / Left / Right) with the right garment shot per tab.
      // Fully-qualified CDN URLs — never persist a proxy-prefixed URL.
      views: color.views ? {
        front: unwrapImg(color.views.front) || primaryImage,
        back:  unwrapImg(color.views.back)  || primaryImage,
        left:  unwrapImg(color.views.left)  || primaryImage,
        right: unwrapImg(color.views.right) || primaryImage,
      } : {
        front: primaryImage, back: primaryImage, left: primaryImage, right: primaryImage,
      },
      basePrice: Number(selectedStyle.basePrice || 0),
      // Per-size wholesale pricing from SanMar CSV — lets the pricing engine
      // apply real 2XL/3XL/4XL upcharges instead of a hardcoded matrix.
      sizePrices: selectedStyle.sizePrices || {},
      sizes: selectedStyle.sizes || [],
      supplier: selectedStyle.supplier || 'SanMar',
    });
  };

  const handleImgError = (e) => {
    const fallback = e.currentTarget.getAttribute('data-fallback');
    if (fallback && e.currentTarget.src !== fallback) {
      e.currentTarget.src = fallback;
    } else {
      // Both failed – show neutral placeholder
      e.currentTarget.style.opacity = '0.15';
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70 }} onClick={onClose} data-testid="product-picker-modal">
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '95vw', maxWidth: 960, maxHeight: '88vh', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 80px rgba(0,0,0,0.35)' }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>
              {selectedStyle ? `Choose color — ${selectedStyle.brandName} ${selectedStyle.styleID}` : 'Attach Product'}
            </div>
            {selectedStyle && (
              <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>{selectedStyle.styleName}</div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {selectedStyle && (
              <button
                onClick={() => setSelectedStyle(null)}
                data-testid="picker-back-btn"
                style={{ background: '#F1F5F9', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#475569' }}
              >
                ← Back
              </button>
            )}
            <button onClick={onClose} data-testid="picker-close-btn" style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', display: 'flex' }}>
              <X size={20} color="#64748B" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>
          {!selectedStyle && (
            <>
              <div style={{ position: 'relative', marginBottom: 20 }}>
                <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: 14, top: 14 }} />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search SanMar catalog — try 'PC61', 'nike polo', 'sport-tek'…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="picker-search-input"
                  style={{ width: '100%', padding: '12px 14px 12px 40px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 15, color: '#0F172A', outline: 'none' }}
                />
                {loading && <Loader2 size={16} className="animate-spin" style={{ position: 'absolute', right: 14, top: 14, color: '#94A3B8' }} />}
              </div>

              {query.trim().length < 2 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8', fontSize: 14 }}>
                  Type at least 2 characters to search.
                </div>
              )}
              {query.trim().length >= 2 && !loading && results.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8', fontSize: 14 }}>
                  No SanMar products matched "{query}". Try a style code or brand name.
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                {results.map((p) => (
                  <button
                    key={p.styleID}
                    onClick={() => setSelectedStyle(p)}
                    data-testid={`picker-style-${p.styleID}`}
                    style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', padding: 12, textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3B82F6'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(15,23,42,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ height: 130, background: '#F8FAFC', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10, overflow: 'hidden' }}>
                      {p.styleImageUrl ? (
                        <img
                          src={p.styleImageUrl}
                          alt={p.styleName}
                          onError={handleImgError}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <span style={{ color: '#CBD5E1', fontSize: 11 }}>NO IMAGE</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, marginBottom: 3 }}>{p.brandName} · {p.styleID}</div>
                    <div style={{ fontSize: 13, color: '#0F172A', fontWeight: 500, lineHeight: 1.3, minHeight: 34 }}>
                      {p.styleName}
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: '#0F172A', fontWeight: 600 }}>${Number(p.basePrice || 0).toFixed(2)}</span>
                      <span style={{ fontSize: 10, color: '#64748B', background: '#F1F5F9', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>{p.colorCount || p.colors?.length || 0} colors</span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {selectedStyle && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20, marginBottom: 20, padding: 14, background: '#F8FAFC', borderRadius: 8 }}>
                <div style={{ height: 160, background: '#fff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {selectedStyle.styleImageUrl && (
                    <img src={selectedStyle.styleImageUrl} onError={handleImgError} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>{selectedStyle.brandName} · {selectedStyle.styleID}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', marginTop: 2, marginBottom: 8 }}>{selectedStyle.styleName}</div>
                  <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, maxHeight: 74, overflow: 'hidden' }}>{selectedStyle.description}</div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#0F172A', fontWeight: 600 }}>${Number(selectedStyle.basePrice || 0).toFixed(2)} base</span>
                    <span style={{ fontSize: 12, color: '#64748B' }}>Sizes: {(selectedStyle.sizes || []).join(', ') || '—'}</span>
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748B', fontWeight: 600, marginBottom: 10 }}>Pick a Color</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                {(selectedStyle.colors || []).map((c) => {
                  const hex = c.hex && !c.hex.includes('PROCESS') && !c.hex.includes(' ')
                    ? c.hex
                    : '#334155';
                  return (
                  <button
                    key={c.colorName}
                    onClick={() => pickColor(c)}
                    data-testid={`picker-color-${(c.colorName || '').toLowerCase().replace(/\s+/g, '-')}`}
                    style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', padding: 8, textAlign: 'center', transition: 'border-color 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#3B82F6')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E2E8F0')}
                  >
                    <div style={{ position: 'relative', height: 110, background: '#F8FAFC', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, overflow: 'hidden' }}>
                      {(c.imageUrl || selectedStyle.styleImageUrl) && (
                        <img
                          src={c.imageUrl || selectedStyle.styleImageUrl}
                          data-fallback={selectedStyle.styleImageUrl}
                          onError={handleImgError}
                          alt={c.colorName}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      )}
                      {/* Color swatch dot overlaid on the image so the user can
                          tell the colors apart even when SanMar has no per-color
                          photography. */}
                      <span style={{
                        position: 'absolute', bottom: 6, right: 6,
                        width: 22, height: 22, borderRadius: 999,
                        background: hex,
                        border: '2px solid #fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                      }} />
                    </div>
                    <div style={{ fontSize: 12, color: '#0F172A', fontWeight: 500 }}>{c.colorName}</div>
                  </button>
                );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductPickerModal;
